import { useState } from "react";
import { motion } from "framer-motion";
import {
  Lightning,
  Check,
  ArrowClockwise,
  Lightbulb,
  Trophy,
  Fire,
  SealCheck,
  CheckCircle,
  Star,
} from "@phosphor-icons/react";
import { SectionShell } from "../../styleguide/Primitives";
import { Showcase } from "../LibraryPrimitives";
import { BloomButton } from "../../styleguide/ui/BloomButton";
import { BloomProgress } from "../../styleguide/ui/BloomProgress";
import { Mascot } from "../../styleguide/Mascot";

const MOODS = [
  { mood: "happy", label: "Happy" },
  { mood: "thinking", label: "Thinking" },
  { mood: "encouraging", label: "Encouraging" },
  { mood: "celebrating", label: "Celebrating" },
];

const TIMINGS = [
  { ms: "120–200ms", t: "Quick interactions", d: "Taps, toggles, selections" },
  { ms: "300ms", t: "Standard transitions", d: "Cards, reveals, navigation" },
  { ms: "500ms", t: "Celebrations", d: "Milestones & rewards" },
];

function FeedbackDemo() {
  const [state, setState] = useState("idle");
  const [progress, setProgress] = useState(40);

  const correct = () => { setState("correct"); setProgress((p) => Math.min(100, p + 20)); };
  const wrong = () => setState("wrong");
  const reset = () => { setState("idle"); setProgress(40); };

  return (
    <div className="space-y-5">
      <motion.div
        animate={state === "wrong" ? { x: [0, -8, 8, -5, 5, 0] } : { x: 0 }}
        transition={{ duration: 0.4 }}
        className={`flex items-center gap-3 rounded-2xl border-2 p-4 ${
          state === "correct" ? "border-teal bg-teal/12" : state === "wrong" ? "border-destructive bg-destructive/10" : "border-border bg-card"
        }`}
      >
        <span className={`flex h-10 w-10 items-center justify-center rounded-xl font-extrabold ${
          state === "correct" ? "bg-teal text-teal-foreground" : state === "wrong" ? "bg-destructive text-destructive-foreground" : "bg-muted text-muted-foreground"
        }`}>
          {state === "correct" ? <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 500, damping: 15 }}><Check size={20} weight="bold" /></motion.span> : "B"}
        </span>
        <span className="text-lg font-bold">42</span>
      </motion.div>

      {state !== "idle" && (
        <motion.p initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className={`font-bold ${state === "correct" ? "text-teal-ink" : "text-primary-strong"}`}>
          {state === "correct" ? "Yes! +20 progress 🎉" : "Not quite — have another go, you've got this!"}
        </motion.p>
      )}

      <BloomProgress value={progress} color="teal" label="Lesson progress" showValue />

      <div className="flex flex-wrap gap-3">
        <BloomButton variant="teal" leftIcon={Check} onClick={correct} data-testid="motion-correct">Mark correct</BloomButton>
        <BloomButton variant="outline" leftIcon={ArrowClockwise} onClick={wrong} data-testid="motion-wrong">Gentle shake</BloomButton>
        <BloomButton variant="ghost" onClick={reset}>Reset</BloomButton>
      </div>
    </div>
  );
}

const COMPLETIONS = [
  { icon: CheckCircle, color: "bg-teal/12 text-teal-ink", t: "Lesson complete", d: "All questions answered" },
  { icon: Lightning, color: "bg-primary/12 text-primary-strong", t: "Daily challenge done", d: "+50 XP earned" },
  { icon: Fire, color: "bg-primary/12 text-primary-strong", t: "5-day streak!", d: "Keep the flame going" },
  { icon: SealCheck, color: "bg-secondary/25 text-secondary-ink", t: "Achievement unlocked", d: "Word Wizard" },
];

export function LibraryMotionSection() {
  return (
    <SectionShell
      id="motion"
      eyebrow="Learning feedback & motion"
      eyebrowIcon={Lightning}
      title="Motion as encouragement"
      description="Animation should feel like a teacher's warm nod — quick and gentle for everyday moments, joyful for real wins, and never punishing for mistakes. All of it respects reduced-motion."
    >
      <div className="space-y-8">
        <Showcase title="Mascot states" description="Happy · Thinking · Encouraging · Celebrating" tone="muted">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {MOODS.map(({ mood, label }) => (
              <div key={mood} className="flex flex-col items-center gap-2 rounded-2xl bg-card p-4 shadow-soft">
                <Mascot size={92} mood={mood} animated={mood !== "thinking"} />
                <span className="text-sm font-bold">{label}</span>
              </div>
            ))}
          </div>
        </Showcase>

        <div className="grid gap-5 lg:grid-cols-2">
          <Showcase title="Correct & incorrect" description="Try it — celebrate or gently nudge">
            <FeedbackDemo />
          </Showcase>

          <Showcase title="Completion states">
            <div className="grid gap-3">
              {COMPLETIONS.map((c) => (
                <div key={c.t} className="flex items-center gap-3 rounded-2xl border border-border bg-card p-4 shadow-soft">
                  <span className={`flex h-11 w-11 items-center justify-center rounded-2xl ${c.color}`}>
                    <c.icon size={22} weight="fill" />
                  </span>
                  <div className="flex-1">
                    <p className="font-display font-extrabold">{c.t}</p>
                    <p className="text-sm font-semibold text-muted-foreground">{c.d}</p>
                  </div>
                  <Star size={18} weight="fill" className="text-secondary" />
                </div>
              ))}
            </div>
          </Showcase>
        </div>

        <div className="grid gap-5 lg:grid-cols-[1.4fr_1fr]">
          <Showcase title="Motion principles">
            <div className="grid gap-4 sm:grid-cols-3">
              {TIMINGS.map((t) => (
                <div key={t.t} className="rounded-2xl bg-muted/40 p-4">
                  <p className="font-display text-xl font-extrabold text-primary-strong">{t.ms}</p>
                  <p className="mt-1 font-bold">{t.t}</p>
                  <p className="text-sm font-semibold text-muted-foreground">{t.d}</p>
                </div>
              ))}
            </div>
          </Showcase>

          <Showcase title="Hint glow">
            <div className="flex h-full flex-col items-center justify-center rounded-2xl border-2 border-secondary bg-card p-6 text-center shadow-soft ring-4 ring-secondary/20">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-secondary/25 text-secondary-ink motion-safe:animate-wiggle">
                <Lightbulb size={28} weight="fill" />
              </span>
              <p className="mt-3 font-bold">Hints glow softly to invite — never to pressure.</p>
            </div>
          </Showcase>
        </div>

        <div className="flex items-center gap-3 rounded-3xl border border-border bg-accent/50 p-5">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary text-primary-foreground"><Trophy size={22} weight="fill" /></span>
          <p className="font-medium text-foreground"><strong>Reduced-motion ready:</strong> when a child (or their device) prefers calm, animations fall back to gentle fades and static states — the meaning never depends on movement.</p>
        </div>
      </div>
    </SectionShell>
  );
}

export default LibraryMotionSection;
